import { groupTimeline, parsePayload } from "./groupTimeline";
import { classifyEventDirection } from "./classifyEventDirection";
import { computeEventDiff } from "../computeEventDiff";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — serialize the correlated timeline into a compact,
// readable text block for the 🤖 Claude conclusion. Pure; reuses the same
// grouping the UI shows so Claude sees exactly what the operator sees.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const NOISE = new Set(["history", "_mongoUsername", "_lastChangedAt", "_lastChangedBy", "_changeSource"]);
const MAX_ROWS = 80;
const MAX_CHARS = 40000;

function etLabel(ms) {
  if (ms == null) return "(undated)";
  const d = new Date(ms); // ms carries the Eastern wall clock; format as UTC to read it back
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC", month: "numeric", day: "numeric", year: "2-digit",
    hour: "numeric", minute: "2-digit", second: "2-digit",
  }).format(d) + " ET";
}

function eventLine(raw) {
  const dir = classifyEventDirection(raw);
  const op = raw.operation === "create" ? "Created" : raw.operation === "delete" ? "Deleted" : "Edited";
  const who = raw.changedBy || raw._modifitionAcct || raw._modificationAccount || "unknown";
  const diff = computeEventDiff(raw)
    .filter((d) => !NOISE.has(d.key))
    .map((d) => `${d.key} ${fmt(d.before)}→${fmt(d.after)}`)
    .join(", ");
  const flow = dir.dir === "out" ? "Scheduler→FileMaker" : "FileMaker→Scheduler";
  return `${op} by ${who} [${flow}]${diff ? ` — ${diff}` : " — (no business change)"}`;
}
function scheduleLine(raw) {
  const parts = [];
  if (raw.StatusCodeBefore != null || raw.StatusCodeAfter != null) parts.push(`status ${raw.StatusCodeBefore ?? "—"}→${raw.StatusCodeAfter ?? "—"}`);
  if (raw.ActivityType) parts.push(raw.ActivityType);
  if (raw.StartDate) parts.push(`start ${raw.StartDate}`);
  if (raw.InstallerNo) parts.push(`installer ${raw.InstallerNo}`);
  if (String(raw.IsCurrent) === "1") parts.push("CURRENT");
  return parts.join(", ") || "(schedule row)";
}
function turboLine(raw) {
  const ackErr = raw.error != null && String(raw.error) !== "" && String(raw.error) !== "0";
  const r = parsePayload(raw.result);
  const badStatus = String(raw.status) === "11";
  const failed = ackErr || badStatus || (r && r.errorCode != null && r.errorCode !== 0);
  const reason = r && (r.descriptor || r.errorMessage) ? (r.descriptor || r.errorMessage) : "";
  return `${raw.channel || "?"} — ${failed ? `FAILED${reason ? ` "${reason}"` : ""}` : "ok"}`;
}
function fmt(v) {
  if (v === undefined) return "(absent)";
  if (v === null) return "null";
  if (v === "") return '""';
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

/**
 * @param {{doc:object, eventLog:object[], schedule:object[], turboq:object[], schedulerOnly:boolean}} p
 * @returns {string}
 */
export function buildConclusionContext({ doc, eventLog = [], schedule = [], turboq = [], schedulerOnly = true } = {}) {
  const lines = [];
  if (doc) {
    const cust = `${doc.customer_firstName ?? ""} ${doc.customer_lastName ?? ""}`.trim();
    lines.push("JOB CURRENT STATE:");
    lines.push(`  invoice ${doc.invoiceNo ?? "?"} · customer ${cust || "?"} · company ${doc.companyCode ?? "?"} · location ${doc.location ?? "?"} · status ${doc.statusCode ?? "?"} · installer ${Array.isArray(doc.installer) ? doc.installer.join(",") : doc.installer ?? "?"} · kellerId ${doc._kf_KellerID ?? "?"}`);
    lines.push("");
  }

  const rows = groupTimeline({ eventLog, schedule, turboq }, { order: "asc", schedulerOnly });
  lines.push(`CORRELATED TIMELINE — ${rows.length} row(s), oldest first. Each row = one logical event (EventLog change / FileMaker Schedule row / TurboQ message):`);

  const shown = rows.slice(0, MAX_ROWS);
  shown.forEach((row, i) => {
    lines.push(`\n[${i + 1}] ${etLabel(row.ms)}`);
    row.cells.eventLog.forEach((it) => lines.push(`  EventLog: ${eventLine(it.raw)}`));
    row.cells.schedule.forEach((it) => lines.push(`  Schedule[DATA]: ${scheduleLine(it.raw)}`));
    row.cells.turboq.forEach((it) => lines.push(`  TurboQ: ${turboLine(it.raw)}`));
  });
  if (rows.length > MAX_ROWS) lines.push(`\n…(${rows.length - MAX_ROWS} more rows omitted)`);

  let text = lines.join("\n");
  if (text.length > MAX_CHARS) text = text.slice(0, MAX_CHARS) + "\n…(truncated)";
  return text;
}
